import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { WATCH_PARTY_EVENTS } from '@/config/watchPartyData';
import { Trash2, Ban } from 'lucide-react';

export default function WatchPartyAdmin() {
  const { slug } = useParams();
  const event = WATCH_PARTY_EVENTS[slug];
  const [session, setSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [messages, setMessages] = useState([]);
  const [rsvpCount, setRsvpCount] = useState(0);
  const [banCount, setBanCount] = useState(0);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setSession(data.session);
        // Check admin via RPC
        supabase.rpc('is_admin').then(({ data: admin }) => setIsAdmin(!!admin));
      }
    });
  }, []);

  useEffect(() => {
    if (!isAdmin || !slug) return;
    // Load messages (including soft-deleted for admin view)
    supabase.from('watch_party_messages').select('*').eq('event_slug', slug).order('created_at', { ascending: true })
      .then(({ data }) => data && setMessages(data));
    // RSVP count
    supabase.from('watch_party_rsvps').select('id', { count: 'exact', head: true }).eq('event_slug', slug)
      .then(({ count }) => setRsvpCount(count || 0));
    // Ban count
    supabase.from('watch_party_bans').select('id', { count: 'exact', head: true }).eq('event_slug', slug)
      .then(({ count }) => setBanCount(count || 0));
    // Realtime
    const ch = supabase.channel(`admin-${slug}`).on('postgres_changes', { event: '*', schema: 'public', table: 'watch_party_messages', filter: `event_slug=eq.${slug}` }, () => {
      supabase.from('watch_party_messages').select('*').eq('event_slug', slug).order('created_at', { ascending: true }).then(({ data }) => data && setMessages(data));
    }).subscribe();
    return () => supabase.removeChannel(ch);
  }, [isAdmin, slug]);

  // Soft-delete: set deleted_at
  const deleteMessage = async (id) => {
    await supabase.from('watch_party_messages').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    setMessages(prev => prev.map(m => m.id === id ? { ...m, deleted_at: new Date().toISOString() } : m));
  };

  const banUser = async (userId) => {
    await supabase.from('watch_party_bans').upsert({ event_slug: slug, user_id: userId, banned_by: session.user.email }, { onConflict: 'user_id,event_slug' });
    setBanCount(c => c + 1);
  };

  if (!event) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a', color: '#888' }}>Event not found.</div>;
  if (!session) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a', color: '#888' }}>Not logged in. <Link to={`/watch-party/${slug}`} style={{ color: '#C9A961', marginLeft: 8 }}>Go to event</Link></div>;
  if (!isAdmin) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a', color: '#888' }}>Access denied.</div>;

  return (
    <div className="min-h-screen p-5 md:p-10" style={{ background: '#0a0a0a', color: '#E8E2D2' }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-xl italic" style={{ color: '#C9A961', fontFamily: 'Georgia, serif' }}>Admin: {event.title}</h1>
          <Link to={`/watch-party/${slug}`} className="text-xs" style={{ color: '#888173', textDecoration: 'none' }}>← Back to room</Link>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          {[{ label: 'RSVPs', value: rsvpCount, color: '#C9A961' }, { label: 'Messages', value: messages.length, color: '#C9A961' }, { label: 'Bans', value: banCount, color: '#993C1D' }].map(s => (
            <div key={s.label} className="p-4 rounded-sm" style={{ background: '#1a1a1a', border: '1px solid rgba(201,169,97,0.08)' }}>
              <p className="text-[10px] tracking-wider uppercase" style={{ color: '#888173', fontFamily: 'Inter' }}>{s.label}</p>
              <p className="text-2xl font-bold mt-1" style={{ color: s.color, fontFamily: 'Georgia' }}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="rounded-sm overflow-hidden" style={{ border: '1px solid rgba(201,169,97,0.08)' }}>
          <div className="px-4 py-3" style={{ background: '#1a1a1a', borderBottom: '1px solid rgba(201,169,97,0.06)' }}>
            <span className="text-[11px] tracking-wider uppercase" style={{ color: '#888173' }}>Messages</span>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {messages.map(msg => (
              <div key={msg.id} className={`flex items-start gap-3 px-4 py-3 group hover:bg-white/[0.01] ${msg.deleted_at ? 'opacity-30' : ''}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs italic" style={{ color: '#C9A961', fontFamily: 'Georgia' }}>{msg.display_name}</span>
                    <span className="text-[10px]" style={{ color: '#5C5851' }}>{new Date(msg.created_at).toLocaleTimeString()}</span>
                    {msg.deleted_at && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: '#993C1D', color: '#E8E2D2' }}>deleted</span>}
                  </div>
                  <p className="text-sm mt-0.5" style={{ color: '#E8E2D2' }}>{msg.body}</p>
                </div>
                {!msg.deleted_at && (
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => deleteMessage(msg.id)} title="Delete" style={{ color: '#993C1D' }}><Trash2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => banUser(msg.user_id)} title="Ban user" style={{ color: '#993C1D' }}><Ban className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
